import Arrays from "utils/Arrays";
import BulkActions from "const/BulkActions";
import BusinessesActions from "actions/BusinessesActions";
import CommentTargetTypes from "const/CommentTargetTypes";
import CommentsActions from "actions/CommentsActions";
import TransactionsActions from "actions/TransactionsActions";

const {
  FETCH_TRANSACTIONS_LIST_START,
  FETCH_TRANSACTIONS_TAGS_LIST_START,
  FETCH_TRANSACTIONS_REASONS_LIST_START,
  ADD_NEW_TRANSACTION_START,
  EDIT_TRANSACTION_START,
  DELETE_TRANSACTION_START,
  BULK_TRANSACTIONS_UPDATE_START,
  FETCH_TRANSACTIONS_LIST_DONE,
  FETCH_TRANSACTIONS_TAGS_LIST_DONE,
  FETCH_TRANSACTIONS_REASONS_LIST_DONE,
  ADD_NEW_TRANSACTION_DONE,
  EDIT_TRANSACTION_DONE,
  DELETE_TRANSACTION_DONE,
  BULK_TRANSACTIONS_UPDATE_DONE,
  FETCH_TRANSACTIONS_LIST_ERROR,
  FETCH_TRANSACTIONS_TAGS_LIST_ERROR,
  FETCH_TRANSACTIONS_REASONS_LIST_ERROR,
  ADD_NEW_TRANSACTION_ERROR,
  EDIT_TRANSACTION_ERROR,
  DELETE_TRANSACTION_ERROR,
  BULK_TRANSACTIONS_UPDATE_ERROR,
  LOCK_TRANSACTION,
  UNLOCK_TRANSACTION,
  ADD_ATTACHMENT_DONE,
  ADD_ATTACHMENT_ERROR,
  REMOVE_ATTACHMENT_DONE,
  REMOVE_ATTACHMENT_ERROR
} = TransactionsActions;

const { UNPAIR_MATCHES_DONE, SELECT_BUSINESS } = BusinessesActions;

const { MARK_ALL_AS_READ_DONE, ADD_NEW_COMMENT_START, FETCH_COMMENTS_DONE } = CommentsActions;

const initialState = {
  fetchingData: false,
  fetchingTags: false,
  fetchingReasons: false,
  dataHash: null,
  stats: {},
  data: [],
  tags: [],
  reasons: [],
  locked: {}
};

const getStatsState = ({ stats, status: statuses }) => {
  const currentStats = statuses
    ? (Array.isArray(statuses) ? statuses : [statuses]).reduce((result, status) => {
      const item = stats[status];

      if (item?.count) {
        result.count += item.count;
      }

      return result;
    }, { count: 0 })
    : stats.total;

  return {
    stats: {
      ...Object.fromEntries(Object.entries(stats).map(([key, value]) => [key, value.count])),
      current: currentStats.count
    }
  };
};

export default (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case FETCH_TRANSACTIONS_LIST_START:
      return {
        ...state,
        ...(payload.clearList ? { data: [], stats: {}, dataHash: null } : {}),
        ...(payload.backgroundUpdate ? {} : { fetchingData: true })
      };

    case FETCH_TRANSACTIONS_TAGS_LIST_START:
      return { ...state, fetchingTags: true };

    case FETCH_TRANSACTIONS_REASONS_LIST_START:
      return { ...state, fetchingReasons: true };

    case ADD_NEW_TRANSACTION_START:
    case EDIT_TRANSACTION_START:
    case DELETE_TRANSACTION_START:
    case BULK_TRANSACTIONS_UPDATE_START:
      return {
        ...state,
        ...(payload?.backgroundUpdate ? {} : { fetchingData: true })
      };

    case FETCH_TRANSACTIONS_LIST_DONE:

      return {
        ...state,
        ...(!payload.dataHash || payload.dataHash !== state.dataHash ? {
          ...getStatsState(payload),
          data: payload.transactions,
          dataHash: payload.dataHash || null
        } : {}),
        ...(payload.backgroundUpdate ? {} : { fetchingData: false })
      };

    case FETCH_TRANSACTIONS_TAGS_LIST_DONE:
      return { ...state, tags: payload.tags, fetchingTags: false };

    case FETCH_TRANSACTIONS_REASONS_LIST_DONE:
      return { ...state, reasons: payload.reasons, fetchingReasons: false };

    case EDIT_TRANSACTION_DONE: {
      return {
        ...state,
        data: state.data.map((transaction) => (transaction.id === payload.transaction.id ? payload.transaction : transaction)),
        fetchingData: false
      };
    }
    case DELETE_TRANSACTION_DONE: {
      return {
        ...state,
        data: state.data.filter(({ id }) => id !== payload.transactionId),
        fetchingData: false
      };
    }
    case BULK_TRANSACTIONS_UPDATE_DONE: {
      if ([BulkActions.REMOVE, BulkActions.RESET_AND_REDO].includes(payload.action)) {
        return {
          ...state,
          data: state.data.map((transaction) => (payload.transactionIds.includes(transaction.id)
            ? { ...transaction, deleted: true } : transaction)),
          fetchingData: false
        };
      }

      return state;
    }
    case UNPAIR_MATCHES_DONE: {
      return {
        ...state,
        data: state.data.map((transaction) => (transaction.id === payload.transactionId
          ? { ...transaction, documentId: null } : transaction)),
        fetchingData: false
      };
    }
    case ADD_NEW_COMMENT_START: {
      const { targetType, targetId, text, createdBy } = payload;

      if (targetType !== CommentTargetTypes.TRANSACTIONS) return state;

      return {
        ...state,
        data: Arrays.updateItemById(state.data, targetId, (transaction) => {
          const { all = 0, ...rest } = transaction.comments || {};

          return {
            ...transaction,
            comments: { ...rest, all: all + 1 },
            lastComment: { text, createdBy }
          };
        })
      };
    }
    case FETCH_COMMENTS_DONE: {
      const { targetType, targetId } = payload;

      if (targetType !== CommentTargetTypes.TRANSACTIONS) return state;

      return {
        ...state,
        data: state.data.map((transaction) => {
          if (transaction.id !== targetId) return transaction;

          const { all = 0 } = transaction.comments || {};

          return {
            ...transaction,
            comments: { all, unread: 0 }
          };
        })
      };
    }

    case ADD_ATTACHMENT_DONE: {
      const { transactionId, attachmentId, attachmentName, attachmentKey } = payload;

      return {
        ...state,
        data: state.data.map((transaction) => (transaction.id === transactionId
          ? { ...transaction, attachmentId, attachmentName, attachmentKey } : transaction))
      };
    }

    case REMOVE_ATTACHMENT_DONE: {
      const { transactionId } = payload;

      return {
        ...state,
        data: state.data.map((transaction) => (transaction.id === transactionId
          ? { ...transaction, attachmentId: null, attachmentName: null, attachmentKey: null } : transaction))
      };
    }

    case ADD_NEW_TRANSACTION_DONE:
    case FETCH_TRANSACTIONS_LIST_ERROR:
    case ADD_NEW_TRANSACTION_ERROR:
    case EDIT_TRANSACTION_ERROR:
    case DELETE_TRANSACTION_ERROR:
    case BULK_TRANSACTIONS_UPDATE_ERROR:
    case ADD_ATTACHMENT_ERROR:
    case REMOVE_ATTACHMENT_ERROR:
      return { ...state, fetchingData: false };

    case FETCH_TRANSACTIONS_TAGS_LIST_ERROR:
      return { ...state, fetchingTags: false };

    case MARK_ALL_AS_READ_DONE:
      return {
        ...state,
        data: state.data.map((item) => ({
          ...item,
          comments: item.comments && { ...item.comments, unread: 0 }
        }))
      };

    case LOCK_TRANSACTION:
      return { ...state, locked: { ...state.locked, [payload]: true } };

    case UNLOCK_TRANSACTION:
      return { ...state, locked: { ...state.locked, [payload]: false } };

    case FETCH_TRANSACTIONS_REASONS_LIST_ERROR:
      return { ...state, fetchingReasons: false };

    case SELECT_BUSINESS: {
      return { ...state, data: [], stats: {}, dataHash: null };
    }

    default:
      return state;
  }
};
